import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
  Pagination,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import variables from "../../../assets/styles/variables.module.scss";
import CreateMultiAgent from "../CreateMultiAgent";
import MultiAgentCard from "../../../Shared-ui/SharedComponents/Cards/MultiAgentCard";
import { useDispatch, useSelector } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { TEAMS } from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { MultiAgent } from "../../../Shared-ui/Types/MultiAgent";
import Confirm from "../../../Shared-ui/Types/Confirm";
import ConfirmPopup from "../../../Shared-ui/SharedComponents/ConfirmPopup";
import SearchIcon from "@mui/icons-material/Search";
import Agent_emptystateimage from "../../../assets/images/Agents_emptystateimage.svg";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { NumberDomain } from "recharts/types/util/types";
import { set } from "date-fns";

// Styled Button
const StyledColorButton = styled(Button)(({ theme }) => ({
  color: variables.whiteColor,
  backgroundColor: variables.primaryButton,
  "&:hover": {
    backgroundColor: variables.primaryColor,
  },
  textTransform: "capitalize",
  fontWeight: 400,
  borderRadius: "8px",
  height: "40px",
  whiteSpace: "nowrap",
}));

const MultiAgents: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  const [multiAgents, setMultiAgents] = useState<MultiAgent[]>([]);
  const [multiAgentsAll, setMultiAgentsAll] = useState<MultiAgent[]>([]);
  const [multiAgent, setMultiAgent] = useState<MultiAgent | null>(null);
  const [confirmPopupData, setConfirmPopupData] = useState<Confirm>({
    show: false,
  });
  const [searchText, setSearchText] = useState<string>("");
  // State for opening dialog

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(999);
  const prevPage = useRef<number>(page);
  const pageLimitRef = useRef(8);

  useEffect(() => {
    getMultiAgents();
  }, []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterType, setFilterType] = useState<string>("All Agents");
  const [filteredMultiAgents, setFilteredMultiAgents] = useState<MultiAgent[]>([]);
  const loaderStatus = useSelector((state: any) => state.LoaderReducer);

  const paginatedAgents = (data: MultiAgent[]) => {
    const dataPaginated = data.slice(
      (page - 1) * pageLimitRef.current,
      page * pageLimitRef.current
    );
    setMultiAgents(dataPaginated);
  };

  useEffect(() => {
    if (prevPage.current !== page) {
      // getMultiAgents();
      paginatedAgents(filteredMultiAgents);
      prevPage.current = page;
    }
  }, [page]);

  useEffect(() => {
    if (filteredMultiAgents.length > 0 && multiAgents.length == 0 && page !== 1) {
      setPage(page - 1);
    }
}, [filteredMultiAgents, multiAgents, page]);

  useEffect(() => {
    handleSearch();
  }, [searchText]);

  const getMultiAgents = async (filter?: string) => {
    try {
      dispatch(StartLoading("MultiAgent"));
      var url;
      if (filter) {
        url = `${TEAMS}?skip=${0}&limit=${limit}&filter=${filter}`;
      } else {
        url = `${TEAMS}?skip=${0}&limit=${limit}&filter=all`;
      }
      const data = await BaseInstance.get(url).then((res) => res?.data);
      // setMultiAgents(data)
      data.sort(
        (a: { created_at: string }, b: { created_at: string }) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setMultiAgentsAll(data);
      setFilteredMultiAgents(data);
      paginatedAgents(data);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading("MultiAgent"));
    }
  };
  const updateMultiAgentStatus = async (agent: MultiAgent) => {
    try {
      dispatch(StartLoading());
      const data = await BaseInstance.post(
        `${TEAMS}/${agent.id}/toggle-status`
      ).then((res) => res?.data);
      getMultiAgents();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  };

  const deleteAgent = async (agent: MultiAgent) => {
    try {
      setConfirmPopupData((preVal) => ({ ...preVal, show: false }));
      dispatch(StartLoading());
      const data = await BaseInstance.delete(`${TEAMS}/${agent.id}`).then(
        (res) => res?.data
      );
      getMultiAgents();
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading());
    }
  };

  const confirmDelete = (agent: MultiAgent) => {
    setConfirmPopupData({
      show: true,
      setConfirmPopupData: setConfirmPopupData,
      title: "Confirm Delete",
      info: "Are you sure want to delete the Multi-Agent? This action cannot be undone.",
      varient: "delete",
      functionName: deleteAgent,
      functionParam: agent,
    });
  };
  const handleClose = () => {
    setOpen(false);
    getMultiAgents();
    setMultiAgent(null);
  };

  const editMultiAgent = (multiAgent: MultiAgent) => {
    setMultiAgent(multiAgent);
    setOpen(true);
  };

  const handleOpen = () => setOpen(true);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleSearch = () => {
    if (!searchText) {
      setPage(1);
      setFilteredMultiAgents(multiAgentsAll);
      paginatedAgents(multiAgentsAll);
    } else {
      const filtered = multiAgentsAll.filter((agent) =>
        [agent.name].some((field: string) =>
          field.toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setPage(1);
      setFilteredMultiAgents(filtered);
      paginatedAgents(filtered);
    }
  };

  const handleFilterPopUp = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClick = (option: number) => {
    if (option == 0) {
      getMultiAgents("all");
      setFilterType("All agents")
    } else if (option == 1) {
      getMultiAgents("owned");
      setFilterType("Created by me")
    } else if (option == 2) {
      getMultiAgents("shared");
      setFilterType("Shared")
    }
    setAnchorEl(null);
  };

  const getFilterIcon = (type: string) => {
    switch (type) {
      case "Created by me":
        return <PersonOutlineIcon sx={{ fontSize: 20 }} />;
      case "Shared":
        return <ShareOutlinedIcon sx={{ fontSize: 20 }} />;
      default:
        return <PeopleOutlineIcon sx={{ fontSize: 20 }} />;
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: { xs: 2, sm: 2, md: 0 },
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography>Filter by:</Typography>
            <Button
              onClick={handleFilterPopUp}
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={getFilterIcon(filterType)}
              sx={{
                textTransform: "none",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                display: "flex",
                alignItems: "center",
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                },
              }}
            >
              {filterType}
            </Button>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            sx={{
              "& .MuiPaper-root": {
                width: 220,
                boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                borderRadius: "8px",
                mt: 1,
              },
            }}
          >
            <MenuItem onClick={() => handleFilterClick(0)}>
              <PeopleOutlineIcon sx={{ mr: 1, fontSize: 20 }} />
              <Box sx={{ lineHeight: 0 }}>
                <Typography sx={{ lineHeight: "16px" }}>All Agents</Typography>
                <Typography variant="caption" color="text.secondary">
                  View all available agents
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleFilterClick(1)}>
              <PersonOutlineIcon sx={{ mr: 1, fontSize: 20 }} />
              <Box sx={{ lineHeight: 0 }}>
                <Typography sx={{ lineHeight: "16px" }}>
                  Created by me
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Agents I created
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleFilterClick(2)}>
              <ShareOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
              <Box sx={{ lineHeight: 0 }}>
                <Typography sx={{ lineHeight: "16px" }}>Shared</Typography>
                <Typography variant="caption" color="text.secondary">
                  Agents shared
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            width: { xs: "100%", sm: "100%", md: "auto" },
          }}
        >
          <TextField
            placeholder="Search Multi-Agent"
            variant="outlined"
            size="small"
            sx={{
              width: { xs: "100%", sm: "350px" },
              flex: { xs: 1, sm: 1, md: "none" },
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            value={searchText}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch} // your custom function
                    edge="end"
                    size="small"
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <StyledColorButton
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            size={isSmallScreen ? "small" : "large"}
            onClick={handleOpen}
            sx={{
              "& .MuiButton-startIcon": {
                mr: { xs: 0, sm: 1 },
              },
            }}
          >
            {!isSmallScreen && "Create Multi-Agent"}
          </StyledColorButton>
        </Box>
      </Box>

      {/* Agent Cards */}
      {multiAgents.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xl: "1fr 1fr 1fr 1fr",
              lg: "1fr 1fr 1fr 1fr",
              md: "1fr 1fr 1fr",
              sm: "1fr 1fr",
            },
            gap: 3,
            gridAutoRows: "1fr",
          }}
        >
          {multiAgents.map((agent, index) => (
            <MultiAgentCard
              key={index}
              multiAgent={agent}
              deleteFunc={confirmDelete}
              editFun={editMultiAgent}
              statusChangeFunc={updateMultiAgentStatus}
            />
          ))}
        </Box>
      )}
      {multiAgentsAll.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mt: 6,
            // mb: 6,
          }}
        >
          <img
            src={Agent_emptystateimage}
            alt="No Agents"
            style={{ width: "200px", marginBottom: "20px" }}
          />
          {loaderStatus.isLoading === false &&
            loaderStatus.pageName === "MultiAgent" && (
              <Typography variant="h6" color="text.secondary">
                No Multi-Agents Found
              </Typography>
            )}
          {loaderStatus.isLoading === true &&
            loaderStatus.pageName === "MultiAgent" && (
              <Typography variant="h6" color="text.secondary">
                Multi-Agents loading...
              </Typography>
            )}
        </Box>
      )}

      {multiAgentsAll.length > 0 && multiAgents.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
          }}
        >
          <img
            src={Agent_emptystateimage}
            alt="No Agents"
            style={{ width: "200px", marginBottom: "20px" }}
          />
          <Typography variant="h6" color="text.secondary">
            No Multi-Agents Found
          </Typography>
        </Box>
      )}
      {/* Pagination */}
      {filteredMultiAgents.length > pageLimitRef.current && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <Pagination
            count={Math.ceil(filteredMultiAgents.length / pageLimitRef.current)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
        </Box>
      )}

      {/* Create Team Dialog */}
      {/* Create Team Dialog */}
      <CreateMultiAgent
        open={open}
        handleClose={handleClose}
        multiAgent={multiAgent}
      />
      <ConfirmPopup details={confirmPopupData} />
    </Box>
  );
};

export default MultiAgents;
